'use client';

import { useEffect, useState } from 'react';
import { Bookmark, Share2 } from 'lucide-react';

import { cn } from '@/components/ui/utils';

const SAVED_PREFIX = 'mnrq:saved-branch:';

/**
 * The two icon actions the Chow place screen carries beside the branch name.
 *
 * Save is device-local on purpose: a public visitor may have no account, and a
 * bookmark that asked them to sign in would be a worse control than no
 * bookmark. The stored value is only the branch id, so clearing site data
 * clears the save with it.
 */
export function GuestV2SaveShare({
  branchId,
  branchName,
}: {
  branchId: string;
  branchName: string;
}) {
  const [saved, setSaved] = useState(false);
  const [status, setStatus] = useState<string | null>(null);

  useEffect(() => {
    try {
      setSaved(window.localStorage.getItem(`${SAVED_PREFIX}${branchId}`) === '1');
    } catch {
      setSaved(false);
    }
  }, [branchId]);

  const toggleSaved = () => {
    const next = !saved;
    setSaved(next);
    try {
      if (next) window.localStorage.setItem(`${SAVED_PREFIX}${branchId}`, '1');
      else window.localStorage.removeItem(`${SAVED_PREFIX}${branchId}`);
    } catch {
      // Private mode can refuse storage; the button still gives immediate
      // feedback for this visit, which is better than silently doing nothing.
    }
    setStatus(next ? `${branchName} saved on this device.` : `${branchName} removed from saved.`);
    window.setTimeout(() => setStatus(null), 2500);
  };

  const share = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({ title: branchName, url });
        return;
      }
      await navigator.clipboard.writeText(url);
      setStatus('Link copied.');
      window.setTimeout(() => setStatus(null), 2500);
    } catch {
      // A cancelled share sheet is not an error worth announcing.
    }
  };

  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={toggleSaved}
        aria-pressed={saved}
        aria-label={saved ? `Remove ${branchName} from saved` : `Save ${branchName}`}
        className={cn('gv-icon-btn', saved && 'is-saved')}
      >
        <Bookmark aria-hidden="true" className={cn('size-5', saved && 'fill-current')} />
      </button>
      <button
        type="button"
        onClick={() => void share()}
        aria-label={`Share ${branchName}`}
        className="gv-icon-btn"
      >
        <Share2 aria-hidden="true" className="size-5" />
      </button>
      <span className="sr-only" role="status" aria-live="polite">{status}</span>
    </div>
  );
}
